import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Tracker } from '../models/evaluation/tracker.model';
import { Subject, Observable } from 'rxjs';
import { AccountService } from './account.service';
import { AlertService, MessageSeverity } from './alert.service';
import { TeamSelectionViewModel } from '../models/team/team.model';
import * as signalR from '@microsoft/signalr';
/*import { MatSnackBar } from '@angular/material/snack-bar';*/

@Injectable({
  providedIn: 'root'
})
export class PACTHubService {

  private hubConnection: signalR.HubConnection;

  private consensusState$: Subject<Array<Tracker>> = new Subject<Array<Tracker>>();
  private consensusTitleState$: Subject<string> = new Subject<string>();
  private consensusSubmittedMessageState$: Subject<string> = new Subject<string>();
  private requestedConsensusState$: Subject<Boolean> = new Subject<Boolean>();
  private consensusStarted$: Subject<Boolean> = new Subject<Boolean>();
  private isStoppingConnection: boolean = false;
  //private consensusPresent$: Subject<Array<string>>;

  constructor(private authService: AuthService, private accountService: AccountService, private alertService: AlertService
    //, private snackbarService: SnackbarService
  ) {
    this.consensusState$ = new Subject<Array<Tracker>>();
    this.requestedConsensusState$ = new Subject<Boolean>();
    this.consensusStarted$ = new Subject<Boolean>();
    //this.consensusPresent$ = new Subject<Array<string>>();
  }

  public getConsensusState(): Observable<Array<Tracker>> {
    return this.consensusState$.asObservable();
  }

  public getConsensusTitle(): Observable<string> {
    return this.consensusTitleState$.asObservable();
  }

  public getConsensusSubmittedMessage(): Observable<string> {
    return this.consensusSubmittedMessageState$.asObservable();
  }

  public getConsensusStarted(): Observable<Boolean> {
    return this.consensusStarted$.asObservable();
  }

  public getRequestedConsensusState(): Observable<Boolean> {
    return this.requestedConsensusState$.asObservable();
  }

  public removeOldConnection(): Promise<void> {
    if (this.isStoppingConnection) {
      return Promise.resolve();
    }

    this.isStoppingConnection = true;

    if (!this.hubConnection) {
      this.isStoppingConnection = false;
      return Promise.resolve();
    }

    return this.hubConnection.stop()
      .then(() => {
        this.hubConnection = null;
        console.log("Old connection stopped successfully.");
      })
      .catch(err => {
        console.log("Err: Stopping old connection => " + err)
        this.hubConnection = null;
      }).
      finally(() => {
        this.isStoppingConnection = false;
      });
  }

  public startConnection = () => {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl('/hub/pact', { accessTokenFactory: () => this.authService.accessToken })
      .withAutomaticReconnect()
      .build();
    this.hubConnection.start()
      .then(() => {
        console.log("Connection to hub started");
        this.joinPresence();
        /*this.snackbarService.warning("Connection to hub started.", "Dismiss");*/
        //this.alertService.showStickyMessage("Connection to hub started.", `Connection to hub started.`, MessageSeverity.warn);
      })
      .catch(err => {
        if (this.authService.isLoggedIn) {
          this.alertService.showStickyMessage("Connection problem detected.", `SignalR connection problem detected.  Failed to start connection. If issue persists, please hard refresh (CTRL + SHIFT + R) your browser.`, MessageSeverity.error);
          console.log("Err: Connection to hub error => " + err)
        }
      });

    this.hubConnection.onreconnecting(error => {
      console.warn("SignalR Connection lost due to error. Reconnecting...", error);
    });

    this.hubConnection.onreconnected(connectionId => {
      console.log("SignalR Connection re-established. Connected with connectionId: ", connectionId);
    });

    this.hubConnection.onclose(error => {
      if (error) {
        // Connection closed due to an error
        console.error("SignalR Connection closed with error: ", error);
        /*this.snackbarService.warning("Connection problem detected.  Attempting to reconnect. If issue persists, please refresh your browser.", "Dismiss");*/
        this.alertService.showStickyMessage("Connection problem detected.", `SignalR connection problem detected.  Attempting to reconnect. If issue persists, please hard refresh (CTRL + SHIFT + R) your browser.`, MessageSeverity.warn);
      } else {
        // Connection closed gracefully (e.g., via connection.stop())
        console.log("SignalR Connection closed gracefully.");
        //this.snackbarService.warning("Connection closed gracefully.", "Dismiss");
        // this.alertService.showStickyMessage("Idle time limit passed.", `Automatically logged out.`, MessageSeverity.warn);
      }
    });
  }

  public joinPresence = () => {
    this.hubConnection.invoke('JoinPresence')
      .then(() => console.log("Sent JoinPresence"))
      .catch(err => console.log("JoinPresence => " + err));

    this.hubConnection.on('JoinPresence', (message) => {
      console.log(`JoinPresence: ${message}`);
      this.broadcastPresence();
    });

    this.hubConnection.on('BroadcastPresence', (message) => {
      console.log(`BroadcastPresence: ${message}`);
    });

    this.hubConnection.on('LeavePresence', (message) => {
      console.log(`LeavePresence: ${message}`);
    });

    this.hubConnection.on('BroadcastTeamsUpdated', (uid) => {
      console.log(`Received BroadcastTeamsUpdated for uid ${uid}`);
      if (this.accountService.currentUser.id == uid) {
        console.log("BroadcastTeamsUpdated uid match. Attempting teams update.");
        if (this.accountService.GetActiveProject != null && this.accountService.GetActiveRole() != null) {
          this.accountService.GetCurrentUserTeamsObservable().subscribe({
            next: success => {
              console.log("Successfully queried user teams.");
              this.alertService.showMessage("Teams Updated", "A supervisor has updated your teams list.", MessageSeverity.success);

              var currentTeams: TeamSelectionViewModel[] = this.accountService.GetCurrentUserTeams();
              var activeTeam = this.accountService.GetActiveTeam();

              if (activeTeam == null) {
                if (currentTeams != null && currentTeams.length > 0) {
                  this.accountService.SetActiveTeam(currentTeams[0]);

                  this.alertService.showStickyMessage("Default Team Selected", `You've been assigned to team ${currentTeams[0].teamId}: ${currentTeams[0].teamMembers}. If you are on a page where you need functionality for your team, please refresh it.`, MessageSeverity.success);
                }
              } else if (currentTeams == null || currentTeams.length == 0) {
                this.accountService.SetActiveTeam(null);

                this.alertService.showStickyMessage("No More Teams", `You were just removed from your only team. If you are on a page that includes team functionality, please refresh it.`, MessageSeverity.success);
              }
            },
            error: (error) => {
              console.log("BroadcastTeamsUpdated handler failed to poll for current user teams.");
            }
          });
        }
      }
    });
  }

  public leavePresence = () => {
    this.hubConnection.invoke('LeavePresence')
      .then(() => console.log("Sent LeavePresence"))
      .catch(err => console.log("Err: LeavePresence => " + err));

    this.hubConnection.off('JoinPresence');
    this.hubConnection.off('BroadcastPresence');
    this.hubConnection.off('LeavePresence');
  }

  public broadcastPresence = () => {
    this.hubConnection.invoke('BroadcastPresence')
      .then(() => console.log("Sent BroadcastPresence"));
  }

  public joinConsensus = (evaluationId, requestState?: boolean) => {
    this.hubConnection.invoke('JoinConsensus', evaluationId)
      .then(() => console.log("Sent JoinConsensus " + evaluationId))
      .then(() => { if (requestState) this.broadcastRequestConsensusState(evaluationId); })
      .catch(err => console.log("Err: JoinConsensus => " + err));

    this.hubConnection.on('JoinConsensus', (message) => {
      console.log(`JoinConsensus: ${message}`);
      this.broadcastConsensusPresence(evaluationId);
    });

    this.hubConnection.on('BroadcastConsensusPresence', (message) => {
      console.log(`BroadcastConsensusPresence: ${message}`);
    });

    this.hubConnection.on('LeaveConsensus', (message) => {
      console.log(`LeaveConsensus: ${message}`);
    });

    this.hubConnection.on('BroadcastConsensusState', (state, title, submittedMessage) => {
      console.log(`Received BroadcastConsensusState: ${state.length} objects`);
      //console.log(state);
      this.consensusState$.next(state);
      this.consensusTitleState$.next(title);
      this.consensusSubmittedMessageState$.next(submittedMessage);
      this.consensusStarted$.next(true);
    });

    this.hubConnection.on('RequestBroadcastConsensusState', (message) => {
      console.log(`Received RequestBroadcastConsensusState from ${message}`);
      this.requestedConsensusState$.next(true);
    });
  }

  public leaveConsensus = (evaluationId) => {
    this.hubConnection.invoke('LeaveConsensus', evaluationId)
      .then(() => console.log("Sent LeaveConsensus"))
      .catch(err => console.log("Err: LeaveConsensus => " + err));

    this.hubConnection.off('JoinConsensus');
    this.hubConnection.off('BroadcastConsensusPresence');
    this.hubConnection.off('BroadcastConsensusState');
    this.hubConnection.off('LeaveConsensus');
  }

  public broadcastConsensusPresence = (evaluationId) => {
    this.hubConnection.invoke('BroadcastConsensusPresence', evaluationId)
      .then(() => console.log("Sent BroadcastConsensusPresence"));
  }

  public broadcastConsensusState = (evaluationId, state, title, submittedMessage) => {
    this.hubConnection.invoke('BroadcastConsensusState', evaluationId, state, title, submittedMessage)
      .then(() => console.log("Sent BroadcastConsensusState"));
  }

  public broadcastRequestConsensusState = (evaluationId) => {
    this.hubConnection.invoke("RequestBroadcastConsensusState", evaluationId)
      .then(() => console.log("Sent RequestBroadcastConsensusState " + evaluationId));
  }

  public broadcastTeamsUpdated = (userId) => {
    this.hubConnection.invoke("BroadcastTeamsUpdated", userId)
      .then(() => console.log("Sent BroadcastTeamsUpdated for uid " + userId));
  }
}
